import React, { useEffect, useRef } from 'react';
import { FaExclamationCircle, FaCheckCircle, FaInfoCircle, FaTimes } from 'react-icons/fa';

type ConfirmModalProps = {
  isOpen: boolean;
  type?: 'danger' | 'warning' | 'info' | 'success';
  title?: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  icon?: React.ReactNode;
  hideCancel?: boolean;
};

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  type = 'danger',
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  icon,
  hideCancel = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onCancel();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onCancel]);

  // Focus trap for accessibility
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Type configurations
  const typeConfigs = {
    danger: {
      icon: icon || <FaExclamationCircle className="h-6 w-6" />,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      confirmBg: 'bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500',
      borderColor: 'border-red-100',
    },
    warning: {
      icon: icon || <FaExclamationCircle className="h-6 w-6" />,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      confirmBg: 'bg-amber-600 hover:bg-amber-700 focus:ring-2 focus:ring-amber-500',
      borderColor: 'border-amber-100',
    },
    info: {
      icon: icon || <FaInfoCircle className="h-6 w-6" />,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      confirmBg: 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500',
      borderColor: 'border-blue-100',
    },
    success: {
      icon: icon || <FaCheckCircle className="h-6 w-6" />,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      confirmBg: 'bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-500',
      borderColor: 'border-green-100',
    },
  };

  const config = typeConfigs[type];

  return (
    <>
      {/* Backdrop with blur */}
      <div 
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        style={{ 
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* Modal */}
      <div 
        className="fixed inset-0 z-[60] overflow-y-auto"
        aria-labelledby="modal-title"
        aria-modal="true"
        role="dialog"
      >
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <div 
            ref={modalRef}
            className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all w-full max-w-md"
            style={{
              opacity: isOpen ? 1 : 0,
              transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(-20px) scale(0.95)',
              transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
          >
            {/* Close button (X) */}
            <button
              onClick={onCancel}
              className="absolute right-3 top-3 z-10 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-500 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
              aria-label="Close"
            >
              <FaTimes className="h-4 w-4" />
            </button>

            {/* Modal content */}
            <div className="p-6">
              <div className="flex flex-col items-center text-center">
                {/* Icon */}
                <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${config.iconBg} mb-4`}>
                  <div className={config.iconColor}>
                    {config.icon}
                  </div>
                </div>

                {/* Title */}
                {title && (
                  <h3 
                    id="modal-title"
                    className="text-lg font-semibold text-gray-900 mb-2"
                  >
                    {title}
                  </h3>
                )}

                {/* Message */}
                <div className="mt-2">
                  <div className="text-sm text-gray-600 leading-relaxed">
                    {message}
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className={`px-6 py-4 flex ${hideCancel ? 'justify-center' : 'justify-end'} gap-3 border-t ${config.borderColor}`}>
              {!hideCancel && (
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={onCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelText}
                </button>
              )}
              <button
                type="button"
                disabled={isLoading}
                onClick={onConfirm}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${config.confirmBg} transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[80px]`}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </>
                ) : (
                  confirmText
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfirmModal;